import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Clock, User as UserIcon } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface MeSessionSettings {
  idleTimeoutMinutes: number;
  tenantDefaultMinutes: number;
  userOverrideMinutes: number | null;
}

/**
 * Personal session preferences. Today the only knob is the idle
 * timeout; future preferences (locale override, theme, notification
 * digest cadence) land here too.
 */
export function MyProfilePage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<MeSessionSettings | null>(null);
  const [override, setOverride] = useState<string>('');
  const [useOverride, setUseOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<MeSessionSettings>('/me/session-settings')
      .then(({ data }) => {
        setSettings(data);
        setUseOverride(data.userOverrideMinutes != null);
        setOverride(
          (data.userOverrideMinutes ?? data.tenantDefaultMinutes).toString(),
        );
      })
      .catch(() => {
        setError(t('profile.error_load'));
      });
  }, [t]);

  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(handle);
  }, [toast]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const value = useOverride
        ? Math.max(0, Math.min(1440, parseInt(override, 10) || 0))
        : null;
      const { data } = await apiClient.patch<MeSessionSettings>(
        '/me/session-settings',
        { idleTimeoutMinutes: value },
      );
      setSettings(data);
      setUseOverride(data.userOverrideMinutes != null);
      setOverride(
        (data.userOverrideMinutes ?? data.tenantDefaultMinutes).toString(),
      );
      setToast(t('profile.session_saved'));
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('profile.error_save'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <UserIcon className="h-5 w-5 text-primary" />
          {t('profile.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('profile.subtitle')}
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-1 flex items-center gap-2 text-base font-medium">
          <Clock className="h-4 w-4" />
          {t('profile.session_section')}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {t('profile.session_explainer')}
        </p>

        {settings && (
          <p className="mb-3 text-sm">
            {t('profile.tenant_default_label')}:{' '}
            <span className="font-medium">
              {settings.tenantDefaultMinutes === 0
                ? t('profile.idle_disabled')
                : t('profile.minutes', { n: settings.tenantDefaultMinutes })}
            </span>
          </p>
        )}

        <form onSubmit={save} className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={useOverride}
              onChange={(e) => setUseOverride(e.target.checked)}
              className="h-4 w-4"
            />
            {t('profile.use_override')}
          </label>

          {useOverride && (
            <div className="space-y-1 pl-6">
              <label className="text-sm font-medium">
                {t('profile.idle_minutes_label')}
              </label>
              <Input
                type="number"
                min="0"
                max="1440"
                step="5"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                {t('profile.idle_minutes_help')}
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
